import cn from '@integrations/classnames'
import { NavLink } from '@integrations/routing'
import {
  CustomerRoles,
  isOwnerOrAdministrator,
  paths,
  useGetUnreadTicketsQuery,
  useReferralStatisticsQry
} from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import styles from '../Header.module.scss'

const activeClassName = ({ isActive }: { isActive: boolean }) => cn({ 'active-tab': isActive })

interface Props {
  hideMenu: () => void
  isForexMenuVisible: boolean
  isDevelopersMenuVisible: boolean
  isReferralMenuVisible: boolean
  customerRole: string
}

// Mounted only when the referral menu is allowed, so the statistics request is never made for other users
function ReferralNavLink({ hideMenu }: { hideMenu: () => void }) {
  const { statistics, statisticsLoading } = useReferralStatisticsQry()

  // Campaign disabled by the backend: by design the whole feature is hidden from the user.
  // Wait for the first response so the link never flashes for a disabled campaign.
  if (statisticsLoading || statistics?.enabled === false) {
    return null
  }

  return (
    <NavLink className={activeClassName} to={paths.referralProgram()} onClick={hideMenu}>
      {tr('FRONTEND.HEADER.REFER_AND_EARN', 'Refer & Earn')}
    </NavLink>
  )
}

function MainMenuItems({
  hideMenu,
  isForexMenuVisible,
  isDevelopersMenuVisible,
  isReferralMenuVisible,
  customerRole
}: Props) {
  const { data: unreadTickets } = useGetUnreadTicketsQuery(undefined, { pollingInterval: 30_000 })
  const hasUnread = (unreadTickets?.length ?? 0) > 0

  return (
    <>
      <NavLink className={activeClassName} to={paths.dashboard()} onClick={hideMenu}>
        {tr('FRONTEND.HEADER.OVERVIEW', 'Overview')}
      </NavLink>
      <NavLink className={activeClassName} to={paths.banking.accounts.list()} onClick={hideMenu}>
        {tr('FRONTEND.HEADER.ACCOUNTS', 'Accounts')}
      </NavLink>
      <NavLink className={activeClassName} to={paths.banking.transactions.list()} onClick={hideMenu}>
        {tr('FRONTEND.HEADER.TRANSACTIONS', 'Transactions')}
      </NavLink>
      {/* Expense Management hidden - may be re-enabled later */}
      {/* <NavLink activeClassName='active-tab' to={paths.banking.expenses()} onClick={hideMenu}>
        {tr('FRONTEND.HEADER.EXPENSES', 'Expenses')}
      </NavLink> */}
      {isForexMenuVisible && (
        <NavLink className={activeClassName} to={paths.forex()} onClick={hideMenu}>
          {tr('FRONTEND.HEADER.FOREX', 'Forex')}
        </NavLink>
      )}
      {customerRole === CustomerRoles.OWNER && (
        <NavLink className={activeClassName} to={paths.paymentMethods()} onClick={hideMenu}>
          {tr('FRONTEND.HEADER.PAYMENT_METHODS', 'Payment Methods')}
        </NavLink>
      )}
      {isOwnerOrAdministrator(customerRole) && (
        <NavLink className={activeClassName} to={paths.userManagement()} onClick={hideMenu}>
          {tr('FRONTEND.HEADER.USERS', 'Users')}
        </NavLink>
      )}
      {isReferralMenuVisible && <ReferralNavLink hideMenu={hideMenu} />}
      {isDevelopersMenuVisible && (
        <NavLink className={activeClassName} to={paths.developer()} onClick={hideMenu}>
          {tr('FRONTEND.HEADER.DEVELOPERS', 'Developers')}
        </NavLink>
      )}
      <NavLink
        to={paths.support.tickets()}
        onClick={hideMenu}
        className={({ isActive }) => cn(styles['nav-support'], 'nav-support', { 'active-tab': isActive })}
      >
        {tr('FRONTEND.HEADER.SUPPORT', 'Support')}
        {hasUnread && <span className={cn(styles['nav-unread-dot'], 'nav-unread-dot')} />}
      </NavLink>
    </>
  )
}

export default MainMenuItems
